import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { getCartItems, saveCartItems } from "../../utils/cartUtils";
import {
  getUserData,
  isUserLoggedIn,
  isUserEmailVerified,
} from "../../utils/authUtils";
import { useCanViewPrices } from "../../utils/pricingVisibility";
import { userProfileService } from "../../api/services/userProfileService";
import { configService } from "../../api/services/configService";
import { orderService } from "../../api/services/orderService";
import { itemService } from "../../api/services/itemService";
import {
  getSelectedShippingMethodId,
  getShippingMethodConfig,
  pickShippingRateForMethod,
  SELECTED_SHIPPING_STORAGE_KEY,
  FEDEX_CARRIERS,
} from "../../utils/shippingCutoffConfig";
import StripePaymentModal from "./StripePaymentModal";
import BillingCardModal from "../BillingCardModal/BillingCardModal";
import defaultProductImage from "../../images/Default-Image.jpeg";
import "./Checkout.css";

/* Same country list as Create Account */
const SHIP_TO_COUNTRIES = [
  "United States",
  "Canada",
  "United Kingdom",
  "Australia",
  "Germany",
  "France",
  "Italy",
  "Spain",
  "Netherlands",
  "Belgium",
  "Switzerland",
  "Austria",
  "Sweden",
  "Norway",
  "Denmark",
  "Finland",
  "Japan",
  "South Korea",
  "China",
  "India",
  "Brazil",
  "Mexico",
  "Argentina",
  "Chile",
  "New Zealand",
  "Ireland",
  "Portugal",
  "Poland",
  "Czech Republic",
  "Hungary",
  "Romania",
  "Bulgaria",
  "Croatia",
  "Slovenia",
  "Slovakia",
  "Estonia",
  "Latvia",
  "Lithuania",
  "Greece",
  "Cyprus",
  "Malta",
  "Luxembourg",
  "Iceland",
  "Liechtenstein",
  "Monaco",
  "San Marino",
  "Vatican City",
  "Andorra",
];

const COUNTRY_TO_ISO2 = {
  "United States": "US",
  Canada: "CA",
  "United Kingdom": "GB",
  Australia: "AU",
  Germany: "DE",
  France: "FR",
  Italy: "IT",
  Spain: "ES",
  Netherlands: "NL",
  Belgium: "BE",
  Switzerland: "CH",
  Austria: "AT",
  Sweden: "SE",
  Norway: "NO",
  Denmark: "DK",
  Finland: "FI",
  Japan: "JP",
  "South Korea": "KR",
  China: "CN",
  India: "IN",
  Brazil: "BR",
  Mexico: "MX",
  Argentina: "AR",
  Chile: "CL",
  "New Zealand": "NZ",
  Ireland: "IE",
  Portugal: "PT",
  Poland: "PL",
  "Czech Republic": "CZ",
  Hungary: "HU",
  Romania: "RO",
  Bulgaria: "BG",
  Croatia: "HR",
  Slovenia: "SI",
  Slovakia: "SK",
  Estonia: "EE",
  Latvia: "LV",
  Lithuania: "LT",
  Greece: "GR",
  Cyprus: "CY",
  Malta: "MT",
  Luxembourg: "LU",
  Iceland: "IS",
  Liechtenstein: "LI",
  Monaco: "MC",
  "San Marino": "SM",
  "Vatican City": "VA",
  Andorra: "AD",
};

const FROM_POSTAL_CODE =
  process.env.REACT_APP_FROM_POSTAL_CODE || "76258";

const buildPackageFromCart = async (items) => {
  const itemSpecs = await Promise.all(
    items.map(async (item) => {
      try {
        const response = await itemService.getItemDetailAndRelatedItems(
          item.id,
          1,
          1,
        );
        return { item, spec: response?.Item || null };
      } catch {
        return { item, spec: null };
      }
    }),
  );

  let totalOz = 0;
  let maxLength = 0;
  let maxWidth = 0;
  let maxHeight = 0;

  itemSpecs.forEach(({ item, spec }) => {
    if (!spec) return;
    const qty = item.quantity || 1;
    const ozPerUnit =
      (Number(spec.WeightOz) || 0) + (Number(spec.WeightLb) || 0) * 16;
    totalOz += ozPerUnit * qty;
    maxLength = Math.max(maxLength, Number(spec.Length) || 0);
    maxWidth = Math.max(maxWidth, Number(spec.Width) || 0);
    maxHeight = Math.max(maxHeight, Number(spec.Height) || 0);
  });

  return { totalOz, maxLength, maxWidth, maxHeight };
};

const Checkout = () => {
  const navigate = useNavigate();
  const showPrices = useCanViewPrices();
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeKeyLoading, setStripeKeyLoading] = useState(true);
  const [cartItems, setCartItems] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState("visa");
  const [showCardModal, setShowCardModal] = useState(false);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [cardFormData, setCardFormData] = useState({
    cardNumber: "",
    expirationDate: "",
    securityCode: "",
    firstName: "",
    lastName: "",
    rememberCard: false,
    billingCountry: "United States",
    streetAddress: "",
    streetAddress2: "",
    city: "",
    state: "",
    zipCode: "",
  });
  const [formErrors, setFormErrors] = useState({
    cardNumber: false,
    streetAddress: false,
  });
  const [shippingAddress, setShippingAddress] = useState({
    name: "",
    address: "",
    country: "",
    phone: "",
  });
  const [shipToDetails, setShipToDetails] = useState({
    streetAddress: "",
    city: "",
    state: "",
    postalCode: "",
    country: "United States",
  });
  const [selectedShippingRate, setSelectedShippingRate] = useState(null);
  const [shippingRatesLoading, setShippingRatesLoading] = useState(false);
  const [shippingRatesError, setShippingRatesError] = useState(null);
  const [selectedCutoffMethodId, setSelectedCutoffMethodId] = useState(
    getSelectedShippingMethodId,
  );
  const [defaultShippingAddressId, setDefaultShippingAddressId] =
    useState(null);
  const [showShipToModal, setShowShipToModal] = useState(false);
  const [shipToForm, setShipToForm] = useState({
    RecipientName: "",
    StreetAddress: "",
    City: "",
    State: "",
    PostalCode: "",
    Country: "United States",
    PhoneNumber: "",
    IsDefault: true,
  });
  const [shipToModalLoading, setShipToModalLoading] = useState(false);
  const [shipToSaveLoading, setShipToSaveLoading] = useState(false);
  const [shipToSuccessMessage, setShipToSuccessMessage] = useState("");
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [orderValidationSubmitting, setOrderValidationSubmitting] =
    useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [resendEmailLoading, setResendEmailLoading] = useState(false);
  const [resendEmailMessage, setResendEmailMessage] = useState("");
  const [, setCrossTabAuthTick] = useState(0);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "userData" || e.key === "userLoggedIn") {
        setCrossTabAuthTick((t) => t + 1);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Load cart items from localStorage
  useEffect(() => {
    const items = getCartItems();
    if (items && items.length > 0) {
      const normalizedCart = items.map((item) => ({
        ...item,
        quantity: item.quantity || 1,
        price: item.price || 0,
        name: item.name || item.title || "Unknown Product",
        image: item.image || "",
      }));
      setCartItems(normalizedCart);
    }

    // Listen for cart updates
    const handleCartUpdate = () => {
      const updatedItems = getCartItems();
      if (updatedItems && updatedItems.length > 0) {
        const normalizedCart = updatedItems.map((item) => ({
          ...item,
          quantity: item.quantity || 1,
          price: item.price || 0,
          name: item.name || item.title || "Unknown Product",
          image: item.image || "",
        }));
        setCartItems(normalizedCart);
      } else {
        setCartItems([]);
      }
    };

    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
    };
  }, []);

  // On load: fetch user address list and show default address in Ship to (when logged in)
  useEffect(() => {
    if (!isUserLoggedIn()) return;
    const userData = getUserData();
    const userName = userData?.email || userData?.userName;
    const customerId = userData?.customerId;
    if (!userName || customerId == null || customerId === "") return;
    userProfileService
      .getUserAddressList(customerId, userName)
      .then((list) => {
        const defaultAddr = list.find((a) => a.IsDefault === true);
        if (!defaultAddr) return;
        setDefaultShippingAddressId(defaultAddr.ShippingAddressID);
        const addressLine = [
          defaultAddr.StreetAddress,
          defaultAddr.City,
          defaultAddr.State,
          defaultAddr.PostalCode,
        ]
          .filter(Boolean)
          .join(", ");
        const phone = defaultAddr.PhoneNumber || "";
        setShippingAddress({
          name: defaultAddr.RecipientName?.trim() || "—",
          address: addressLine.trim() || "—",
          country: defaultAddr.Country?.trim() || "—",
          phone: phone
            ? phone.length === 10
              ? `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`
              : phone
            : "—",
        });
        setShipToDetails({
          streetAddress: defaultAddr.StreetAddress?.trim() || "",
          city: defaultAddr.City?.trim() || "",
          state: defaultAddr.State?.trim() || "",
          postalCode: defaultAddr.PostalCode?.trim() || "",
          country: defaultAddr.Country?.trim() || "United States",
        });
      })
      .catch(() => {
        // Keep default/empty on error
      });
  }, []);

  useEffect(() => {
    const syncSelectedCutoffMethod = () => {
      setSelectedCutoffMethodId(getSelectedShippingMethodId());
    };

    const handleShippingMethodChanged = () => {
      syncSelectedCutoffMethod();
    };

    const handleStorage = (event) => {
      if (event.key === SELECTED_SHIPPING_STORAGE_KEY) {
        syncSelectedCutoffMethod();
      }
    };

    window.addEventListener("shippingMethodChanged", handleShippingMethodChanged);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        "shippingMethodChanged",
        handleShippingMethodChanged,
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const selectedCutoffMethod = getShippingMethodConfig(selectedCutoffMethodId);

  useEffect(() => {
    if (!cartItems.length) {
      setSelectedShippingRate(null);
      setShippingRatesError(null);
      return undefined;
    }

    const toPostalCode = shipToDetails.postalCode?.trim();
    const toCity = shipToDetails.city?.trim();
    const toState = shipToDetails.state?.trim();
    const toCountry =
      COUNTRY_TO_ISO2[shipToDetails.country] || shipToDetails.country?.trim();

    if (!toPostalCode || !toCity || !toState || !toCountry) {
      setSelectedShippingRate(null);
      setShippingRatesError(
        "Complete shipping address is required to load shipping rates.",
      );
      return undefined;
    }

    let cancelled = false;
    setShippingRatesLoading(true);
    setShippingRatesError(null);

    (async () => {
      try {
        const packageDetails = await buildPackageFromCart(cartItems);
        if (cancelled) return;

        if (
          packageDetails.totalOz <= 0 ||
          packageDetails.maxLength <= 0 ||
          packageDetails.maxWidth <= 0 ||
          packageDetails.maxHeight <= 0
        ) {
          setSelectedShippingRate(null);
          setShippingRatesError(
            "Cart items are missing weight or dimension data for shipping rates.",
          );
          return;
        }

        const userName = getUserData()?.email || getUserData()?.userName;
        const cutoffMethod = getShippingMethodConfig(selectedCutoffMethodId);
        const carriers = cutoffMethod.carriers?.length
          ? cutoffMethod.carriers
          : FEDEX_CARRIERS;

        const response = await orderService.getShippingRates(
          {
            FromPostalCode: FROM_POSTAL_CODE,
            ToPostalCode: toPostalCode,
            ToCity: toCity,
            ToState: toState,
            ToCountry: toCountry,
            WeightValue: packageDetails.totalOz,
            WeightUnit: "ounces",
            Length: packageDetails.maxLength,
            Width: packageDetails.maxWidth,
            Height: packageDetails.maxHeight,
            DimensionUnit: "inches",
            Carriers: carriers,
            Confirmation: "delivery",
            Residential: true,
          },
          { userName },
        );

        if (cancelled) return;

        const allRates = Array.isArray(response?.rates) ? response.rates : [];
        const matchedRate = pickShippingRateForMethod(allRates, cutoffMethod);

        if (matchedRate) {
          setSelectedShippingRate(matchedRate);
        } else {
          setSelectedShippingRate(null);
          setShippingRatesError(
            allRates.length > 0
              ? `No shipping rate is available for ${cutoffMethod.name} (Cutoff: ${cutoffMethod.cutoffTime}).`
              : "No shipping rates were returned.",
          );
        }
      } catch (error) {
        if (cancelled) return;
        setSelectedShippingRate(null);
        setShippingRatesError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load shipping rates.",
        );
      } finally {
        if (!cancelled) {
          setShippingRatesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cartItems, shipToDetails, selectedCutoffMethodId]);

  const openShipToModal = () => {
    if (defaultShippingAddressId == null || !isUserLoggedIn()) return;
    const userName = getUserData()?.email || getUserData()?.userName;
    if (!userName) return;
    setShowShipToModal(true);
    setShipToModalLoading(true);
    userProfileService
      .getUserAddress(defaultShippingAddressId, userName)
      .then((addr) => {
        setShipToForm({
          RecipientName: addr.RecipientName || "",
          StreetAddress: addr.StreetAddress || "",
          City: addr.City || "",
          State: addr.State || "",
          PostalCode: addr.PostalCode || "",
          Country: addr.Country || "United States",
          PhoneNumber: addr.PhoneNumber || "",
          IsDefault: addr.IsDefault === true,
        });
      })
      .catch(() => {
        setShowShipToModal(false);
      })
      .finally(() => {
        setShipToModalLoading(false);
      });
  };

  const closeShipToModal = () => {
    setShowShipToModal(false);
  };

  const handleShipToFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setShipToForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleShipToSave = () => {
    const userData = getUserData();
    const userName = userData?.email || userData?.userName;
    const customerId = userData?.customerId;
    if (
      !userName ||
      customerId == null ||
      customerId === "" ||
      defaultShippingAddressId == null
    ) {
      setShipToSaveLoading(false);
      return;
    }
    setShipToSaveLoading(true);
    const payload = {
      ShippingAddressID: defaultShippingAddressId,
      CustomerId: Number(customerId) || customerId,
      RecipientName: shipToForm.RecipientName || "",
      StreetAddress: shipToForm.StreetAddress || "",
      City: shipToForm.City || "",
      State: shipToForm.State || "",
      PostalCode: shipToForm.PostalCode || "",
      Country: shipToForm.Country || "United States",
      PhoneNumber: shipToForm.PhoneNumber || "",
      IsDefault: !!shipToForm.IsDefault,
    };
    userProfileService
      .updateUserAddress(payload, userName)
      .then((response) => {
        if (response?.success === 1) {
          setShowShipToModal(false);
          setShipToSuccessMessage("Update successful");
          return userProfileService.getUserAddressList(customerId, userName);
        }
        return Promise.reject(new Error(response?.msg || "Update failed"));
      })
      .then((list) => {
        if (Array.isArray(list)) {
          const defaultAddr = list.find((a) => a.IsDefault === true);
          if (defaultAddr) {
            const addressLine = [
              defaultAddr.StreetAddress,
              defaultAddr.City,
              defaultAddr.State,
              defaultAddr.PostalCode,
            ]
              .filter(Boolean)
              .join(", ");
            const phone = defaultAddr.PhoneNumber || "";
            setShippingAddress({
              name: defaultAddr.RecipientName?.trim() || "—",
              address: addressLine.trim() || "—",
              country: defaultAddr.Country?.trim() || "—",
              phone: phone
                ? phone.length === 10
                  ? `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`
                  : phone
                : "—",
            });
            setShipToDetails({
              streetAddress: defaultAddr.StreetAddress?.trim() || "",
              city: defaultAddr.City?.trim() || "",
              state: defaultAddr.State?.trim() || "",
              postalCode: defaultAddr.PostalCode?.trim() || "",
              country: defaultAddr.Country?.trim() || "United States",
            });
          }
        }
      })
      .catch(() => {
        setShipToSuccessMessage("Update failed. Please try again.");
      })
      .finally(() => {
        setShipToSaveLoading(false);
      });
  };

  // Fetch Stripe publishable key from API (PublishableKey_Test) and init Stripe
  useEffect(() => {
    configService
      .getStripePublishableKey()
      .then((key) => {
        if (key) {
          const maskedKey =
            key.length > 14 ? `${key.slice(0, 10)}...${key.slice(-4)}` : key;
          const keyMode = key.startsWith("pk_test_")
            ? "test"
            : key.startsWith("pk_live_")
              ? "live"
              : "unknown";
          console.log("[Stripe] Publishable key loaded", {
            key: maskedKey,
            mode: keyMode,
          });
          setStripePromise(loadStripe(key));
        } else {
          console.error("[Stripe] Publishable key missing/invalid");
        }
      })
      .catch((err) => {
        console.error("[Stripe] Failed to load publishable key", err);
      })
      .finally(() => setStripeKeyLoading(false));
  }, []);

  // Auto-close red error notification after 3 seconds
  useEffect(() => {
    if (!checkoutError) return;
    const timer = setTimeout(() => setCheckoutError(null), 3000);
    return () => clearTimeout(timer);
  }, [checkoutError]);

  // Update quantity
  const updateQuantity = (itemId, change) => {
    setCartItems((prevItems) => {
      const updated = prevItems.map((item) => {
        if (item.id === itemId) {
          const newQuantity = Math.max(1, item.quantity + change);
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
      saveCartItems(updated);
      return updated;
    });
  };

  // Remove item from cart
  const removeItem = (itemId) => {
    setCartItems((prevItems) => {
      const updated = prevItems.filter((item) => item.id !== itemId);
      saveCartItems(updated);
      return updated;
    });
  };

  const hasValidShippingRate = Boolean(selectedShippingRate);

  // Calculate totals: items subtotal, then 6.5% tax on items subtotal, shipping, order total
  const shippingCost = hasValidShippingRate
    ? Number(selectedShippingRate.TotalCost) || 0
    : 0;
  const itemsTotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const TAX_RATE = 0.065; // 6.5%
  const tax = Math.round(itemsTotal * TAX_RATE * 100) / 100; // sum of 6.5% per item (total tax)
  const orderTotal = itemsTotal + shippingCost + tax;

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(" ");
    } else {
      return v;
    }
  };

  // Format expiration date
  const formatExpirationDate = (value) => {
    const v = value.replace(/\D/g, "");
    if (v.length >= 2) {
      return v.substring(0, 2) + "/" + v.substring(2, 4);
    }
    return v;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;

    // Format card number
    if (name === "cardNumber") {
      processedValue = formatCardNumber(value);
    }
    // Format expiration date
    if (name === "expirationDate") {
      processedValue = formatExpirationDate(value);
    }
    // Only allow numbers for security code
    if (name === "securityCode") {
      processedValue = value.replace(/\D/g, "");
    }

    setCardFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : processedValue,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  // Handle confirm and pay button
  const handleConfirmAndPay = async () => {
    if (isUserLoggedIn() && !isUserEmailVerified()) return;
    if (selectedPayment !== "visa") {
      // Handle other payment methods
      console.log("Processing payment with", selectedPayment);
      return;
    }
    if (!cartItems.length) {
      setCheckoutError("Your cart is empty.");
      return;
    }
    if (shippingRatesLoading) {
      setCheckoutError("Shipping rates are still loading. Please wait.");
      return;
    }
    if (!hasValidShippingRate) {
      setCheckoutError(
        shippingRatesError ||
          "Shipping rates are required before you can confirm and pay.",
      );
      return;
    }

    setCheckoutError(null);
    setOrderValidationSubmitting(true);
    const userData = getUserData();
    const validatePayload = {
      orderItems: cartItems.map((item) => ({
        itemId: Number(item.id ?? item.itemId ?? 0),
        itemName: item.name || item.title || item.itemName || "Item",
        quantity: Number(item.quantity || 0),
      })),
    };

    try {
      const res = await orderService.validateOrderItems(validatePayload, {
        userName: userData?.email || userData?.userName || "",
      });
      const isValid =
        res?.Success === true ||
        res?.success === true ||
        res?.success === 1 ||
        Number(res?.success) === 1;

      if (!isValid) {
        setCheckoutError(
          res?.ErrorMessage ||
            res?.error_msg ||
            res?.error_message ||
            res?.message ||
            res?.msg ||
            "Some items are not valid for checkout right now. Please review your cart.",
        );
        return;
      }

      setShowCardModal(true);
    } catch (err) {
      setCheckoutError(
        err?.response?.data?.ErrorMessage ||
          err?.response?.data?.error_msg ||
          err?.response?.data?.error_message ||
          err?.response?.data?.message ||
          err?.response?.data?.msg ||
          err?.message ||
          "Unable to validate order items. Please try again.",
      );
    } finally {
      setOrderValidationSubmitting(false);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowCardModal(false);
  };

  // Handle add card - validate billing details only; card number is entered securely in Stripe modal (cannot be passed for PCI)
  const handleAddCard = (e) => {
    e.preventDefault();
    const errors = {
      cardNumber: false,
      streetAddress: !cardFormData.streetAddress,
    };
    setFormErrors(errors);

    if (!errors.streetAddress) {
      setShowCardModal(false);
      setShowStripeModal(true);
    }
  };

  // Billing details from card form to pass to Stripe modal (so they reflect there)
  const stripeCountryCode =
    COUNTRY_TO_ISO2[cardFormData.billingCountry] || "US";
  const billingDetailsForStripe = {
    name:
      [cardFormData.firstName, cardFormData.lastName]
        .filter(Boolean)
        .join(" ")
        .trim() || "Cardholder",
    address: {
      line1: cardFormData.streetAddress || "",
      line2: cardFormData.streetAddress2 || "",
      city: cardFormData.city || "",
      state: cardFormData.state || "",
      postal_code: cardFormData.zipCode || "",
      country: stripeCountryCode,
    },
    formatted: [
      [cardFormData.firstName, cardFormData.lastName].filter(Boolean).join(" "),
      cardFormData.streetAddress,
      cardFormData.streetAddress2,
      [cardFormData.city, cardFormData.state, cardFormData.zipCode]
        .filter(Boolean)
        .join(", "),
      cardFormData.billingCountry,
    ]
      .filter(Boolean)
      .join(", "),
  };

  const handleCloseStripeModal = () => {
    setShowStripeModal(false);
    setCardFormData({
      cardNumber: "",
      expirationDate: "",
      securityCode: "",
      firstName: "",
      lastName: "",
      rememberCard: false,
      billingCountry: "United States",
      streetAddress: "",
      streetAddress2: "",
      city: "",
      state: "",
      zipCode: "",
    });
  };
  const handleStripePaymentSuccess = async (stripeResult) => {
    if (isUserLoggedIn() && !isUserEmailVerified()) return;
    console.log("[Stripe] Modal success payload:", stripeResult);
    setShowStripeModal(false);
    setCheckoutError(null);
    setCheckoutSubmitting(true);

    const userData = getUserData();
    const billingName =
      [cardFormData.firstName, cardFormData.lastName]
        .filter(Boolean)
        .join(" ")
        .trim() || "Cardholder";

    const stripeReferenceId = stripeResult?.id || "";
    const stripeCardBrand =
      stripeResult?.card?.brand || stripeResult?.raw?.card?.brand || "0";
    console.log("[Stripe] Checkout charge context", {
      amount: orderTotal,
      currency: "usd",
      stripeReferenceId,
      stripeKind: stripeResult?.kind || "unknown",
      stripeCardBrand,
    });
    let paymentIntentResponse;
    try {
      const stripeReferenceId = stripeResult?.id || "";
      if (!stripeReferenceId) {
        setCheckoutError("Stripe reference missing.");
        setCheckoutSubmitting(false);
        return;
      }
      paymentIntentResponse = await orderService.createPaymentIntent({
        IsTestMode: true,
        Currency: "",
        Amount: Number(orderTotal) || 0,
      });
      console.log("[Stripe] CreatePaymentIntent success", {
        amount: Number(orderTotal) || 0,
        response: paymentIntentResponse,
      });
    } catch (e) {
      console.error("[Stripe] CreatePaymentIntent failed", e);
      setCheckoutError(e.message || "Stripe payment failed.");
      setCheckoutSubmitting(false);
      return;
    }

    const payload = {
      CustomerID: String(userData?.customerId ?? userData?.userId ?? "0"),
      CustomerName: shippingAddress.name || userData?.email || "Guest",
      UserName: userData?.email || "",
      OrderDate: new Date().toISOString(),
      TotalAmount: orderTotal,
      TaxAmount: tax,
      ShippingAmount: shippingCost,
      shippingmethod: selectedShippingRate?.CarrierName || "",
      shippingservice: selectedShippingRate?.ServiceName || "",
      WareHouseID: 0,
      ItemDetail: cartItems.map((item) => ({
        ItemId: item.id,
        ItemCode: String(item.id),
        ItemName: item.name || "Item",
        Quantity: item.quantity || 1,
        UnitPrice: item.price || 0,
        TotalPrice: (item.price || 0) * (item.quantity || 1),
      })),
      PaymentMethodID: 1095,
      CreditCardDetail: {
        CardType: String(stripeCardBrand),
        CardNumber: stripeReferenceId,
        CardHolderName: billingName,
        ExpiryDate: "",
        CVV: "",
        Amount: orderTotal,
        StripeToken: stripeResult?.kind === "token" ? stripeReferenceId : "",
        StripePaymentMethodID:
          stripeResult?.kind === "payment_method" ? stripeReferenceId : "",
      },
      ShippingDetail: {
        RecipientName: shippingAddress.name || "",
        StreetAddress: shipToDetails.streetAddress || shippingAddress.address || "",
        City: shipToDetails.city || "",
        State: shipToDetails.state || "",
        PostalCode: shipToDetails.postalCode || "",
        Country: shipToDetails.country || shippingAddress.country || "",
        PhoneNumber: shippingAddress.phone || "",
      },
      BillingDetail: {
        RecipientName: billingName,
        StreetAddress: cardFormData.streetAddress || "",
        City: cardFormData.city || "",
        State: cardFormData.state || "",
        PostalCode: cardFormData.zipCode || "",
        Country: cardFormData.billingCountry || "",
        PhoneNumber: "",
      },
      PaymentProcesserResponse: {
        IsSuccess: Boolean(stripeReferenceId),
        TransactionID: stripeReferenceId,
        PaymentStatus: stripeReferenceId ? "TOKENIZED" : "FAILED",
        CorrleationID: stripeReferenceId,
        Message: stripeReferenceId ? "TOKENIZED" : "FAILED",
        APIResponse: stripeResult ? JSON.stringify(stripeResult) : "string",
      },
    };

    try {
      console.log("[Stripe] Sending order/checkout payload", {
        totalAmount: payload.TotalAmount,
        taxAmount: payload.TaxAmount,
        shippingAmount: payload.ShippingAmount,
        stripePaymentMethodId: payload.CreditCardDetail.StripePaymentMethodID,
        stripeToken: payload.CreditCardDetail.StripeToken,
        transactionId: payload.PaymentProcesserResponse.TransactionID,
      });
      const res = await orderService.postCheckout(payload, {
        userName: userData?.email || payload.UserName || "",
      });
      console.log("[Stripe] order/checkout response", res);
      const apiData = res?.Data ?? res;
      const isSuccess =
        res?.Success === true ||
        (res?.success !== 0 &&
          (res?.success === true ||
            res?.success === 1 ||
            apiData?.OrderId != null));
      if (!isSuccess) {
        setCheckoutError(
          res?.ErrorMessage ??
            res?.error_msg ??
            res?.error_message ??
            res?.message ??
            "Order could not be placed. Please try again.",
        );
        setCheckoutSubmitting(false);
        return;
      }
      saveCartItems([]);
      window.dispatchEvent(new CustomEvent("cartUpdated", { detail: [] }));
      const orderNumber =
        apiData?.TxnId ??
        apiData?.txnId ??
        res?.TxnId ??
        res?.OrderNumber ??
        res?.orderNumber;
      navigate("/order-success", {
        state: {
          orderId:
            apiData?.OrderId ??
            res?.OrderId ??
            res?.orderId ??
            stripeReferenceId ??
            "—",
          orderNumber: orderNumber ?? String(Math.random()).slice(2, 11),
          status: res?.Status ?? "PENDING",
          orderDate: apiData?.OrderDate
            ? new Date(apiData.OrderDate).toISOString().slice(0, 10)
            : new Date().toISOString().slice(0, 10),
          items: cartItems,
          subtotal: itemsTotal,
          shipping: shippingCost,
          taxes: tax,
          total: orderTotal,
          shippingAddress,
          billingDetail: {
            RecipientName: billingName,
            StreetAddress: cardFormData.streetAddress,
            City: cardFormData.city,
            State: cardFormData.state,
            PostalCode: cardFormData.zipCode,
            Country: cardFormData.billingCountry,
          },
          paymentMethod: "Credit Card (Stripe)",
          apiResponse: apiData,
        },
      });
    } catch (err) {
      setCheckoutError(
        err?.response?.data?.error_msg ||
          err?.response?.data?.error_message ||
          err?.response?.data?.message ||
          err?.message ||
          "Order could not be placed. Please try again.",
      );
    } finally {
      setCheckoutSubmitting(false);
      setCardFormData({
        cardNumber: "",
        expirationDate: "",
        securityCode: "",
        firstName: "",
        lastName: "",
        rememberCard: false,
        billingCountry: "United States",
        streetAddress: "",
        streetAddress2: "",
        city: "",
        state: "",
        zipCode: "",
      });
    }
  };

  const checkoutBlockedUnverified = isUserLoggedIn() && !isUserEmailVerified();

  const handleResendVerificationEmail = async () => {
    const email = getUserData()?.email?.trim();
    if (!email) {
      setResendEmailMessage(
        "We could not find your email. Please sign in again.",
      );
      return;
    }
    setResendEmailLoading(true);
    setResendEmailMessage("");
    try {
      const res = await userProfileService.resendConfirmationEmail(email);
      const ok =
        Number(res?.success) === 1 ||
        res?.success === true ||
        res?.success === "1";
      setResendEmailMessage(
        ok
          ? (res?.msg ?? "Email sent successfully.")
          : (res?.msg ?? "Unable to send email. Please try again."),
      );
    } catch (err) {
      setResendEmailMessage(
        err?.response?.data?.msg ??
          err?.response?.data?.message ??
          "Unable to send email. Please try again.",
      );
    } finally {
      setResendEmailLoading(false);
    }
  };

  if (checkoutBlockedUnverified) {
    return (
      <div className="checkout-details-container checkout-email-blocked">
        <div className="checkout-unverified-card">
          <div className="checkout-unverified-card-header">
            <h2 className="checkout-unverified-title">
              Email verification required
            </h2>
          </div>
          <div className="checkout-unverified-card-body">
            <p className="checkout-unverified-placeholder">
              Email verification is required before you can complete checkout.
              Please use the confirmation prompt shown on screen, or verify your
              email and return to this page.
            </p>
            <div className="checkout-unverified-actions">
              <button
                type="button"
                className="checkout-unverified-resend-btn"
                onClick={handleResendVerificationEmail}
                disabled={resendEmailLoading}
              >
                {resendEmailLoading ? "Sending…" : "Resend email"}
              </button>
            </div>
            {resendEmailMessage ? (
              <p
                className="checkout-unverified-resend-feedback"
                role="status"
                aria-live="polite"
              >
                {resendEmailMessage}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-details-container">
      {checkoutSubmitting && (
        <div className="checkout-submitting-overlay">
          <span>Placing your order...</span>
        </div>
      )}
      {checkoutError && (
        <div className="checkout-error-banner">
          {checkoutError}
          <button
            type="button"
            onClick={() => setCheckoutError(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      {/* Main Content Area - Full Width */}
      <div className="checkout-main-content-full">
        {/* Checkout Title and Image Section */}
        <div className="checkout-header-section">
          <div className="checkout-title-section">
            <h1 className="checkout-title">Pay with</h1>
          </div>
          <div className="checkout-image-showcase">
            <div className="product-showcase-image">
              {/* Image is set as CSS background */}
            </div>
          </div>
        </div>

        {/* Main Content Row - Checkout and Order Summary Side by Side */}
        <div className="checkout-main-row">
          {/* Checkout Form Section - Left Side */}
          <div className="checkout-form-section">
            {/* Payment Options */}
            <div className="payment-options-section">
              <div className="payment-option">
                <input
                  type="radio"
                  id="visa"
                  name="payment"
                  value="visa"
                  checked={selectedPayment === "visa"}
                  onChange={(e) => setSelectedPayment(e.target.value)}
                />
                <label htmlFor="visa" className="payment-label">
                  <div className="payment-logo-box visa-logo-box">
                    <span className="payment-logo-text">VISA</span>
                  </div>
                  <span className="payment-name">Visa</span>
                </label>
              </div>
            </div>

            {/* Ship to Section */}
            <div className="ship-to-section">
              <h2 className="ship-to-title">Ship to</h2>
              <div className="ship-to-address">
                <p>
                  {shippingAddress.name || "—"},{" "}
                  {shippingAddress.address || "—"},{" "}
                  {shippingAddress.country || "—"}
                  {shippingAddress.phone ? `, ${shippingAddress.phone}` : ""}
                </p>
              </div>
              <button
                type="button"
                className="change-address-btn"
                onClick={openShipToModal}
                disabled={!defaultShippingAddressId}
              >
                Change
              </button>
            </div>

            <div className="checkout-shipping-rates-section">
              <h2 className="checkout-shipping-rates-title">Shipping Method</h2>
              <p className="checkout-cutoff-method-note">
                Rates based on selected cutoff:{" "}
                <strong>{selectedCutoffMethod.name}</strong> (Cutoff:{" "}
                {selectedCutoffMethod.cutoffTime})
              </p>
              {shippingRatesLoading && (
                <p className="checkout-shipping-rates-status">
                  Loading shipping rates...
                </p>
              )}
              {!shippingRatesLoading && shippingRatesError && (
                <p className="checkout-shipping-rates-error">
                  {shippingRatesError}
                </p>
              )}
              {!shippingRatesLoading &&
                !shippingRatesError &&
                selectedShippingRate && (
                  <div className="checkout-shipping-rate-selected">
                    <div className="checkout-shipping-rate-details">
                      <span className="checkout-shipping-rate-name">
                        {selectedShippingRate.ServiceName}
                      </span>
                      <span className="checkout-shipping-rate-carrier">
                        {selectedShippingRate.CarrierName}
                      </span>
                    </div>
                    {showPrices && (
                      <span className="checkout-shipping-rate-price">
                        ${Number(selectedShippingRate.TotalCost || 0).toFixed(2)}
                      </span>
                    )}
                  </div>
                )}
            </div>

            {/* Review Order Section */}
            <div className="review-order-section">
              <h2 className="review-order-title">Review Order</h2>
              <div className="review-order-items">
                {cartItems.map((item) => (
                  <div key={item.id} className="review-order-item">
                    <div className="review-item-image">
                      <img
                        src={item.image || defaultProductImage}
                        alt={item.name}
                        className={
                          (item.image || defaultProductImage) ===
                          defaultProductImage
                            ? "review-item-img--default"
                            : ""
                        }
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = defaultProductImage;
                          e.target.classList.add("review-item-img--default");
                        }}
                      />
                    </div>
                    <div className="review-item-info">
                      <div className="review-item-name">{item.name}</div>
                      <button
                        className="review-item-remove-btn"
                        onClick={() => removeItem(item.id)}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="review-item-quantity">
                      <span className="review-quantity-label">Qty</span>
                      <div className="review-quantity-controls">
                        <button
                          className="review-quantity-btn"
                          onClick={() => updateQuantity(item.id, -1)}
                        >
                          -
                        </button>
                        <input
                          type="text"
                          className="review-quantity-input"
                          value={item.quantity}
                          readOnly
                        />
                        <button
                          className="review-quantity-btn"
                          onClick={() => updateQuantity(item.id, 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    {showPrices && (
                      <div className="review-item-price">
                        ${(item.price * item.quantity).toFixed(2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Summary - Right Side */}
          <div className="checkout-order-summary-sidebar">
            <div className="checkout-order-summary-inline">
              {/* Top Bar */}
              <div className="checkout-order-summary-bar"></div>

              <div className="checkout-order-summary-title">Order Summary</div>

              <div className="checkout-summary-items">
                <div className="checkout-summary-item">
                  <div className="checkout-summary-label">
                    Items ({cartItems.length})
                  </div>
                  {showPrices && (
                    <div className="checkout-summary-value">
                      ${itemsTotal.toFixed(2)}
                    </div>
                  )}
                </div>
                <div className="checkout-summary-item">
                  <div className="checkout-summary-label">Shipping</div>
                  {showPrices && (
                    <div className="checkout-summary-value">
                      {hasValidShippingRate
                        ? `$${shippingCost.toFixed(2)}`
                        : "—"}
                    </div>
                  )}
                </div>
                <div className="checkout-summary-item">
                  <div className="checkout-summary-label">Tax</div>
                  {showPrices && (
                    <div className="checkout-summary-value">
                      ${tax.toFixed(2)}
                    </div>
                  )}
                </div>
              </div>

              <div className="checkout-order-total">
                <div className="checkout-total-label">Order Total</div>
                {showPrices && (
                  <div className="checkout-total-amount">
                    ${orderTotal.toFixed(2)}
                  </div>
                )}
              </div>

              <div className="checkout-legal-text">
                By clicking Confirm and Pay, you agree to{" "}
                <span className="checkout-wireox">WireOx</span>{" "}
                <span className="checkout-link checkout-bold">
                  User Agreement
                </span>{" "}
                and acknowledge our{" "}
                <span className="checkout-link checkout-bold">
                  Privacy Notice
                </span>
                .
              </div>

              {!hasValidShippingRate && !shippingRatesLoading && (
                <p className="checkout-shipping-required-error">
                  {shippingRatesError ||
                    "Select a shipping method before confirming your order."}
                </p>
              )}

              <button
                className="confirm-pay-btn"
                onClick={handleConfirmAndPay}
                disabled={
                  orderValidationSubmitting ||
                  checkoutSubmitting ||
                  shippingRatesLoading ||
                  !hasValidShippingRate
                }
              >
                {orderValidationSubmitting
                  ? "Validating..."
                  : shippingRatesLoading
                    ? "Loading shipping..."
                    : "Confirm and Pay"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Ship to – Change address modal */}
      {showShipToModal && (
        <div className="ship-to-modal-overlay" onClick={closeShipToModal}>
          <div
            className="ship-to-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="ship-to-modal-title">Update shipping address</h2>
            {shipToModalLoading ? (
              <p className="ship-to-modal-loading">Loading address…</p>
            ) : (
              <form
                className="ship-to-modal-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleShipToSave();
                }}
              >
                <div className="ship-to-modal-field">
                  <label htmlFor="shipTo-RecipientName">Recipient name</label>
                  <input
                    id="shipTo-RecipientName"
                    type="text"
                    name="RecipientName"
                    value={shipToForm.RecipientName}
                    onChange={handleShipToFormChange}
                  />
                </div>
                <div className="ship-to-modal-field">
                  <label htmlFor="shipTo-StreetAddress">Street address</label>
                  <input
                    id="shipTo-StreetAddress"
                    type="text"
                    name="StreetAddress"
                    value={shipToForm.StreetAddress}
                    onChange={handleShipToFormChange}
                  />
                </div>
                <div className="ship-to-modal-row">
                  <div className="ship-to-modal-field">
                    <label htmlFor="shipTo-City">City</label>
                    <input
                      id="shipTo-City"
                      type="text"
                      name="City"
                      value={shipToForm.City}
                      onChange={handleShipToFormChange}
                    />
                  </div>
                  <div className="ship-to-modal-field">
                    <label htmlFor="shipTo-State">State</label>
                    <input
                      id="shipTo-State"
                      type="text"
                      name="State"
                      value={shipToForm.State}
                      onChange={handleShipToFormChange}
                    />
                  </div>
                  <div className="ship-to-modal-field">
                    <label htmlFor="shipTo-PostalCode">Postal code</label>
                    <input
                      id="shipTo-PostalCode"
                      type="text"
                      name="PostalCode"
                      value={shipToForm.PostalCode}
                      onChange={handleShipToFormChange}
                    />
                  </div>
                </div>
                <div className="ship-to-modal-field">
                  <label htmlFor="shipTo-Country">Country</label>
                  <select
                    id="shipTo-Country"
                    name="Country"
                    value={shipToForm.Country}
                    onChange={handleShipToFormChange}
                  >
                    {SHIP_TO_COUNTRIES.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="ship-to-modal-field">
                  <label htmlFor="shipTo-PhoneNumber">Phone number</label>
                  <input
                    id="shipTo-PhoneNumber"
                    type="text"
                    name="PhoneNumber"
                    value={shipToForm.PhoneNumber}
                    onChange={handleShipToFormChange}
                  />
                </div>
                <label className="ship-to-modal-checkbox">
                  <input
                    type="checkbox"
                    name="IsDefault"
                    checked={shipToForm.IsDefault}
                    onChange={handleShipToFormChange}
                  />
                  Set as default address
                </label>
                <div className="ship-to-modal-actions">
                  <button
                    type="button"
                    className="ship-to-modal-cancel"
                    onClick={closeShipToModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="ship-to-modal-save"
                    disabled={shipToSaveLoading}
                  >
                    {shipToSaveLoading ? "Saving…" : "Save"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Ship to – Update success popup (same style as Create Account success) */}
      {shipToSuccessMessage && (
        <div
          className="checkout-result-popup"
          onClick={() => setShipToSuccessMessage("")}
        >
          <div
            className="checkout-result-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="checkout-result-popup-close"
              onClick={() => setShipToSuccessMessage("")}
              aria-label="Close"
            >
              ×
            </button>
            <p className="checkout-result-popup-message">
              {shipToSuccessMessage}
            </p>
          </div>
        </div>
      )}

      {/* Credit or Debit Card Modal – same as My Account Add New Card */}
      <BillingCardModal
        isOpen={showCardModal}
        onClose={handleCloseModal}
        formData={cardFormData}
        onFormChange={handleInputChange}
        onSubmit={handleAddCard}
        errors={formErrors}
      />

      {/* Stripe Payment Modal - opens after Add on card modal; key loaded from API (PublishableKey_Test) */}
      {showStripeModal && stripePromise ? (
        <Elements stripe={stripePromise}>
          <StripePaymentModal
            onClose={handleCloseStripeModal}
            onSuccess={handleStripePaymentSuccess}
            amount={orderTotal}
            billingDetails={billingDetailsForStripe}
          />
        </Elements>
      ) : showStripeModal && !stripeKeyLoading ? (
        <div className="card-modal-overlay" onClick={handleCloseStripeModal}>
          <div
            className="card-modal-content stripe-config-message"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="card-modal-title">Stripe Not Configured</h2>
            <p className="stripe-config-text">
              Could not load Stripe key from the server. Please try again later.
            </p>
            <div className="card-modal-buttons">
              <button
                className="card-modal-add-btn"
                onClick={handleCloseStripeModal}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : showStripeModal && stripeKeyLoading ? (
        <div className="card-modal-overlay" onClick={handleCloseStripeModal}>
          <div
            className="card-modal-content stripe-config-message"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="stripe-config-text">Loading payment...</p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Checkout;
